import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ORDER_TYPE_OPTIONS } from '../../../../../util/GetOrderFilterOptions';
import { DELIVERY_OPTIONS } from '../../../../../util/Constants';

// Default to delivery (index 1) as the order type for non-bot users.
// date and time are intentionally null at cold-start so consumers can detect
// that restaurant-specific slots have not yet been loaded.
const defaultTypeOption = ORDER_TYPE_OPTIONS[1];

export const INITIAL_STATE = {
  cartItems: [] as any[],
  updatingCartItems: [] as any[],
  selectedMenuItem: null as any,
  fees: [] as any[],
  dynamicFeesValues: {} as any,
  minimums: {} as any,
  // checkoutData is the response from POST /checkout/start (and /adjust).
  // Notable fields used downstream:
  //   - id: string                     – checkout id
  //   - created_at: number             – Unix seconds when the checkout was created
  //   - ttl_seconds?: number           – API-provided checkout TTL in seconds.
  //                                      Source of truth for the staleness window
  //                                      enforced in Checkout.jsx; the constant
  //                                      CHECKOUT_STALE_AGE_MS in util/Constants.js
  //                                      is only a fallback for older API versions.
  //   - isEnded?: boolean              – set client-side after /checkout/end
  checkoutData: null as any,
  checkoutAdjustRequest: null as any,
  checkoutAdjustResponse: null as any,
  checkoutStartResult: null as any,
  coupons: [] as any,
  activeCoupon: {} as any,
  cartRestaurant: null as any,
  isTimeSet: false as boolean,
  notes: '' as string,
  phone: '' as string,
  typeOptions: ORDER_TYPE_OPTIONS as any[],
  type: defaultTypeOption as any,
  date: null as any,
  time: null as any,
  distance: null as any,
  address: null as any,
  locations: [] as any[],
  menuSearchText: '' as string,
  menuSearchRevision: 0,
  menuSearchContext: null as string | null,
  isCheckout: false as boolean,
  forceRedirectHome: false as boolean,
  setCartResponse: null as any,
  cartAttemptId: null as string | null,
  cartStatus: false as boolean,
  isPhoneEditing: false as boolean,
  orderPhoneNumber: null as any,
  deliveryOptions: DELIVERY_OPTIONS[0] as any,
  isMinimumOrder: false as boolean,
  phoneRequired: false as boolean,
  addressRequired: false as boolean,
  deliveryOptionsInstruction: null as any,
  pendingCheckoutOnSignup: false as boolean,
  checkoutContinuation: false as boolean,
  contextRevision: 0,
};

const orderSlice = createSlice({
  name: 'order',
  initialState: INITIAL_STATE,
  reducers: {
    resetOrder(
      state,
      action: PayloadAction<{ preserveTimeIntent?: boolean } | undefined>
    ) {
      return {
        ...state,
        cartItems: [],
        cartRestaurant: null,
        fees: [],
        dynamicFeesValues: {},
        checkoutData: null,
        checkoutAdjustRequest: null,
        checkoutAdjustResponse: null,
        checkoutStartResult: null,
        cartAttemptId: null,
        setCartResponse: null,
        coupons: [],
        activeCoupon: {},
        notes: '',
        phone: '',
        isTimeSet: action.payload?.preserveTimeIntent ? state.isTimeSet : false,
        phoneRequired: false,
        addressRequired: false,
        pendingCheckoutOnSignup: false,
        checkoutContinuation: false,
        contextRevision: (state.contextRevision || 0) + 1,
      };
    },
    setCartItems(state, { payload }) {
      return { ...state, cartItems: payload || [] };
    },
    addUpdatingCartItems(state, { payload }) {
      return {
        ...state,
        updatingCartItems: [...state.updatingCartItems, payload],
      };
    },
    removeUpdatingCartItems(state, { payload }) {
      const updatingCartItems = [...state.updatingCartItems, payload].filter(
        (i) => i !== payload
      );
      return { ...state, updatingCartItems };
    },
    setSelectedMenuItem(state, { payload }) {
      return { ...state, selectedMenuItem: payload };
    },
    setOrderFees(state, { payload }) {
      return { ...state, fees: payload };
    },
    setDynamicFeesValues(state, { payload }) {
      return { ...state, dynamicFeesValues: payload };
    },
    setOrderMinimums(state, { payload }) {
      return { ...state, minimums: payload };
    },
    setCheckoutData(state, { payload }) {
      return { ...state, checkoutData: payload };
    },
    setCoupons(state, { payload }) {
      return { ...state, coupons: payload };
    },
    setActiveCoupon(state, { payload }) {
      return { ...state, activeCoupon: payload };
    },
    setCartRestaurant(state, { payload }) {
      return { ...state, cartRestaurant: payload };
    },
    setNotes(state, { payload }) {
      return { ...state, notes: payload };
    },
    setDeliveryOptionsInstruction(state, { payload }) {
      return { ...state, deliveryOptionsInstruction: payload };
    },
    setDeliveryOptions(state, { payload }) {
      return { ...state, deliveryOptions: payload };
    },
    setPhone(state, { payload }) {
      return { ...state, phone: payload };
    },
    setOrderType(state, { payload }) {
      if (!payload) return state;
      if (payload.value === state.type?.value)
        return { ...state, type: payload };
      return {
        ...state,
        type: payload,
        contextRevision: (state.contextRevision || 0) + 1,
        addressRequired: false,
        checkoutContinuation: false,
        pendingCheckoutOnSignup: false,
        checkoutData: null,
        coupons: [],
      };
    },
    setOrderDate(state, { payload }) {
      return {
        ...state,
        date: payload,
      };
    },
    setOrderDistance(state, { payload }) {
      return {
        ...state,
        distance: payload,
      };
    },
    setOrderTime(state, { payload }) {
      return {
        ...state,
        time: payload,
      };
    },
    checkTimeSet(state, { payload }) {
      return {
        ...state,
        isTimeSet: payload,
      };
    },

    setOrderAddress(state, { payload }) {
      return {
        ...state,
        address: payload,
      };
    },

    setMenuSearchText(state, { payload }) {
      return {
        ...state,
        menuSearchText: payload,
        menuSearchRevision: state.menuSearchRevision + 1,
      };
    },
    setMenuSearchContext(state, { payload }) {
      if (state.menuSearchContext === payload) return state;
      return {
        ...state,
        menuSearchContext: payload,
        menuSearchText: '',
        menuSearchRevision: state.menuSearchRevision + 1,
      };
    },
    menuItemAdded(state, { payload }) {
      // Only the accepted addition that used this exact query may consume it.
      // Cart edits and delayed actions from other searches must not reset it.
      if (
        payload.searchContext !== state.menuSearchContext ||
        payload.searchRevision !== state.menuSearchRevision
      ) {
        return state;
      }
      return {
        ...state,
        menuSearchText: '',
        menuSearchRevision: state.menuSearchRevision + 1,
      };
    },

    setIsCheckout(state, { payload }) {
      return {
        ...state,
        isCheckout: payload,
      };
    },
    setCartResponse(state, { payload }) {
      if (payload?.attemptId && payload.attemptId !== state.cartAttemptId)
        return state;
      return {
        ...state,
        setCartResponse: payload,
      };
    },
    getCart: (state) => state,
    setCart: (state, { payload }) => ({
      ...state,
      cartAttemptId: payload?.attemptId ?? state.cartAttemptId,
    }),
    checkoutStart: (state) => state,
    setCheckoutStartResult: (state, { payload }) => ({
      ...state,
      checkoutStartResult: payload,
    }),
    setCheckoutAdjustRequest(state, { payload }) {
      return {
        ...state,
        checkoutAdjustRequest: payload,
        checkoutAdjustResponse: null,
      };
    },
    setCheckoutAdjustResponse(state, { payload }) {
      return {
        ...state,
        checkoutAdjustRequest: null,
        checkoutAdjustResponse: payload,
      };
    },
    checkoutEnd: (state) => state,
    placeOrder: (state) => state,
    forceRedirect(state, { payload }) {
      return {
        ...state,
        forceRedirectHome: payload,
      };
    },
    updateCartStatus(state, { payload }) {
      return {
        ...state,
        cartStatus: payload,
      };
    },
    updatePhoneEditing(state, { payload }) {
      return {
        ...state,
        isPhoneEditing: payload,
      };
    },
    createOrderPhoneNumber(state, { payload }) {
      return {
        ...state,
        orderPhoneNumber: payload,
      };
    },
    getLocations: (state) => state,
    addLocation(state, { payload }) {
      const alreadyAddedLocation = state.locations.some(
        (location) =>
          location.latitude === payload.latitude &&
          location.longitude === payload.longitude
      );
      if (alreadyAddedLocation) return state;
      return {
        ...state,
        locations: [...state.locations, payload],
      };
    },
    setMinimumOrder(state, { payload }) {
      return {
        ...state,
        isMinimumOrder: payload,
      };
    },
    setPhoneRequired(state, { payload }) {
      return {
        ...state,
        phoneRequired: payload,
      };
    },
    setAddressRequired(state, { payload }) {
      return {
        ...state,
        addressRequired: state.type?.value === 'delivery' && payload,
      };
    },
    setPendingCheckoutOnSignup(state, { payload }) {
      return {
        ...state,
        pendingCheckoutOnSignup: payload,
      };
    },
    setCheckoutContinuation(state, { payload }) {
      return {
        ...state,
        checkoutContinuation: payload,
      };
    },
  },
});

export const {
  resetOrder,
  setCartItems,
  addUpdatingCartItems,
  removeUpdatingCartItems,
  setSelectedMenuItem,
  setOrderFees,
  setDynamicFeesValues,
  setOrderMinimums,
  setCheckoutData,
  setCoupons,
  setActiveCoupon,
  setCartRestaurant,
  setNotes,
  setPhone,
  setOrderType,
  setOrderDate,
  setOrderDistance,
  setOrderTime,
  setOrderAddress,
  setMenuSearchText,
  setMenuSearchContext,
  menuItemAdded,
  setIsCheckout,
  getCart,
  setCart,
  checkoutStart,
  setCheckoutAdjustRequest,
  setCheckoutAdjustResponse,
  checkoutEnd,
  placeOrder,
  forceRedirect,
  addLocation,
  getLocations,
  setCartResponse,
  setCheckoutStartResult,
  checkTimeSet,
  updateCartStatus,
  updatePhoneEditing,
  createOrderPhoneNumber,
  setDeliveryOptions,
  setMinimumOrder,
  setPhoneRequired,
  setAddressRequired,
  setDeliveryOptionsInstruction,
  setPendingCheckoutOnSignup,
  setCheckoutContinuation,
} = orderSlice.actions;

export default orderSlice.reducer;
